import React from 'react';
import { ArrowRightIcon, ArrowUpRightIcon, BadgeCheckIcon, HeartHandshakeIcon, MapPinIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SISTER_ORGANIZATIONS, SisterOrganization } from '../data/sister-organizations';
import { OrgLogo } from './shared/OrgLogo';
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/xhbs9gkKe39dSdAqRNeTtZ/NationalNetworkLogo.png';
export function NationalNetworkSection() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const count = SISTER_ORGANIZATIONS.length;
  return <section id="network" className="w-full bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="network-heading" data-id="element-2849" data-mp="37i7b9aq7jki6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2850" data-mp="3bhoedxi5w5k6">
        {/* Header: logo leads, heading and CTA beside it */}
        <div className="grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center mb-12 lg:mb-16" data-id="element-2851" data-mp="1w4lhtddbewqu">
          <a href="#/national-network" className="block rounded-2xl bg-white border border-haa-navy/10 p-6 sm:p-8 transition-shadow duration-200 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" aria-label={isEs ? 'Ir a la página de la Red Nacional Hondureño-Americana' : 'Go to the Honduran-American National Network page'} data-id="element-2852" data-mp="h2nufvphau7l">
            <img src={NETWORK_LOGO} alt="Honduran-American National Network" className="w-full h-auto max-h-56 object-contain" loading="lazy" decoding="async" data-id="element-2853" data-mp="zzp1dm2lf4e6" />
          </a>

          <div data-id="element-2854" data-mp="14s004lllkk55">
            <h2 id="network-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider leading-none" data-id="element-2855" data-mp="14tb6mt6fg5zy">
              {isEs ? 'Red Nacional' : 'National Network'}
            </h2>
            <p className="font-nunito text-lg text-gray-600 mt-4 max-w-2xl leading-relaxed" data-id="element-2856" data-mp="bfigqrmmxvjy">
              {isEs ? 'Organizaciones hondureño-americanas y aliadas que trabajan juntas para fortalecer comunidades en Estados Unidos y en Honduras. Conozca a quienes forman la red y apoye su labor directamente.' : 'Honduran-American and allied organizations working together to strengthen communities across the United States and in Honduras. Meet the organizations in the network and support their work directly.'}
            </p>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mt-8" data-id="element-2857" data-mp="z3jhvfhhi9pc">
              <a href="#/national-network" className="inline-flex items-center gap-2 rounded-full bg-haa-blue px-6 py-3 font-nunito font-bold text-white transition-colors duration-150 hover:bg-haa-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue focus-visible:ring-offset-2" data-id="element-2858" data-mp="1458xz9jkddka">
                {isEs ? 'Explorar la Red Nacional' : 'Explore the National Network'}
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2859" data-mp="32ey1bcapdaum" />
              </a>
              <p className="font-nunito text-sm text-gray-600" data-id="element-2860" data-mp="2xld6ft4eowzn">
                <span className="font-bebas text-3xl text-haa-navy tracking-wide align-middle mr-2" data-id="element-2861" data-mp="2sn219vp23anr">
                  {count}
                </span>
                {isEs ? 'organizaciones participantes' : 'participating organizations'}
              </p>
            </div>
          </div>
        </div>

        {/* Organizations, pulled directly from the National Network roster */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-id="element-2862" data-mp="3cbkj1s7qflsf">
          {SISTER_ORGANIZATIONS.map(org => <OrganizationCard key={org.name} org={org} isEs={isEs} data-id="element-2863" data-mp="37c7wl8krwgqd" />)}
        </ul>
      </div>
    </section>;
}
type OrganizationCardProps = {
  org: SisterOrganization;
  isEs: boolean;
};
function OrganizationCard({
  org,
  isEs
}: OrganizationCardProps) {
  return <li className="flex flex-col rounded-2xl bg-white border border-haa-navy/10 overflow-hidden" data-id="element-2864" data-mp="3ju0mjese3iu2">
      <div className="flex h-32 items-center justify-center bg-white px-5 border-b border-haa-navy/5" data-id="element-2865" data-mp="3ol8x4e5gha8t">
        {org.logo ? <OrgLogo src={org.logo} alt={`${org.name} logo`} crop={org.logoCrop} scale={org.logoScale} filter={org.logoFilter} boost={org.logoBoost} className="h-24 w-full" lazy data-id="element-2866" data-mp="3av5ahm2nzeiu" /> : <span className="flex h-16 w-16 items-center justify-center rounded-xl font-bebas text-2xl text-white tracking-wide" style={{
        backgroundColor: org.accent
      }} aria-hidden="true" data-id="element-2867" data-mp="bdxdb4lmpm3m">
            {initials(org.name)}
          </span>}
      </div>

      <div className="flex flex-1 flex-col p-5" data-id="element-2868" data-mp="2i3lndr2y5gbk">
        {org.registered501c3 !== false && <p className="flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-700 mb-2" data-id="element-2869" data-mp="2n5b1xgpdoc4e">
            <BadgeCheckIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" data-id="element-2870" data-mp="2aldzyxczvoj0" />
            {isEs ? '501(c)(3) Registrada' : 'Registered 501(c)(3)'}
          </p>}
        <h3 className="font-nunito font-extrabold text-haa-navy text-base leading-snug" data-id="element-2871" data-mp="2oj2nkcyg59rr">
          {org.name}
        </h3>
        <p className="font-nunito text-sm text-haa-blue font-semibold mt-1" data-id="element-2872" data-mp="1vwhnmo3wnpcl">
          {isEs ? org.focusEs : org.focus}
        </p>
        <p className="flex items-center gap-1.5 font-nunito text-sm text-gray-600 mt-2" data-id="element-2873" data-mp="1qy6e6ehivguw">
          <MapPinIcon className="w-3.5 h-3.5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2874" data-mp="25mko19dcw5ky" />
          {org.location}
        </p>

        <div className="mt-auto pt-5 flex flex-wrap gap-2" data-id="element-2875" data-mp="20usy9120y3vv">
          {org.donateUrl ? <a href={org.donateUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#9FD3F5] px-3.5 py-1.5 font-nunito text-xs font-bold text-haa-navy transition-colors duration-150 hover:bg-[#7cc2ef] focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" aria-label={`${isEs ? 'Donar a' : 'Donate to'} ${org.name}`} data-id="element-2876" data-mp="1d4p4rmijvkil">
              <HeartHandshakeIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2877" data-mp="3m130xg9ocaaq" />
              {isEs ? 'Donar' : 'Donate'}
            </a> : null}
          {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-haa-navy px-3.5 py-1.5 font-nunito text-xs font-bold text-white transition-colors duration-150 hover:bg-haa-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" aria-label={`${isEs ? 'Visitar el sitio de' : 'Visit website for'} ${org.name}`} data-id="element-2878" data-mp="2u9dpi1yt6mge">
              {isEs ? 'Sitio web' : 'Website'}
              <ArrowUpRightIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2879" data-mp="so18k13gyidl" />
            </a> : null}
        </div>
      </div>
    </li>;
}
function initials(name: string) {
  return name.split(/\s+/).filter(w => /^[A-ZÁÉÍÓÚÑ]/.test(w)).slice(0, 2).map(w => w[0]).join('');
}